import {
  Router,
  createRouter as _createRouter,
  createWebHistory,
  RouteLocationNormalized,
  NavigationGuardNext,
} from 'vue-router'
import routes from './routes'

import { updateMetaTags } from '@/helpers/router'
import { auth } from '@/services/auth'
import { maybeSyncAnalyticsIds } from '@/analytics/logic'
import { AdjustWrapper } from '@/analytics/adjust'
import { Mixpanel } from '@/analytics/mixpanel'
import { appPositionTrackingService } from '@/services/app.position.tracking'
import { notificationService } from '@/services/notification'
import { backLink } from '@/models/backlink'
import { initWeglotIfNeeded } from '@/services/weglot'
import { createChangelogView } from '@/models/changelog'
import { detectAndSaveMarketingUrl } from '@/analytics/mixpanel/base'
import { platform } from '@/services/platform'
import { Sentry } from '@/services/sentry'
import { backend } from '@/services/backend'

export function createRouter(): Router {
  const router = _createRouter({
    history: createWebHistory(),
    routes: routes,
    scrollBehavior(
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      savedPosition: any,
    ): any {
      if (savedPosition) {
        return savedPosition
      }

      const app = document.querySelector('#app')
      // We already have a scroll position control for `book/` pages/routes.
      // We scroll to the top of the screen on every other page visit except the `book/` page.
      if (app && to.path !== '/' && !to.path.includes('book/')) {
        app.scrollTo(0, 0)
      }

      return { top: 0, left: 0 }
    },
  })

  // Handle navigation to the external billing page
  // for Stripe payments on iOS.
  router.beforeEach(
    async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ) => {
      if (to.name !== 'billing_apple_external') {
        return next()
      }

      // This page should only be accessed
      // via the in-app browser of the iOS app.
      const deviceInfo = await platform.deviceInfo()
      if (deviceInfo.platform !== 'web') {
        Sentry.captureMessage(
          'Navigation to apple external billing page' +
            `on unauthorized platform: ${deviceInfo.platform}`,
        )
        return next({ name: 'billing' })
      }

      const authToken = to.query.authToken16218
      if (authToken) {
        localStorage.setItem('auth_token', authToken as string)

        // Set the user data from the auth token.
        await backend.updateUserData()

        // Remove the auth_token from the URL.
        next({
          name: to.name,
          query: {
            ...to.query,
            authToken16218: undefined,
          },
        })
      } else if (auth.loggedIn()) {
        // The `next` call above will cause this
        // route guard to be triggered again, but
        // this time without the token in the url.
        next()
      } else {
        Sentry.captureMessage(
          'Unexpected navigation to apple external billing page',
        )
        // The user is not logged in,
        // so redirect them to the signup page.
        next({ name: 'signup' })
      }
    },
  )

  router.beforeEach(
    async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ) => {
      const pageName = (to.name as string) || ''
      const isPublicPage = to.meta.isPublicPage as boolean
      const isSignupPage = pageName === 'signup'
      const isLoginPage = pageName === 'login'

      initWeglotIfNeeded()

      // Refresh the changelog data for native devices as we don't have page loads
      if (!isPublicPage && auth.loggedIn()) {
        createChangelogView().then((changelogView) => {
          changelogView.refreshIfNeeded()
        })
      }
      const isUTMEmail = to.query.utm_source && to.query.utm_source === 'email'
      if (!auth.loggedIn() && !isPublicPage) {
        if (auth.previouslyLoggedIn() || isUTMEmail) {
          next({
            name: 'login',
            query: { redirect: to.fullPath },
          })
        } else {
          next({
            name: 'signup',
            query: { redirect: to.fullPath },
          })
        }
      }
      // If we are logged in and try to visit signup/login page
      else if (auth.loggedIn() && (isSignupPage || isLoginPage)) {
        if (auth.isTemporaryUser()) {
          // Let temporary users from the app_browse_15807 experiment visit the auth pages.
          return next()
        }

        detectAndSaveMarketingUrl()
        // We have the `redirect` parameter in the URL query.
        const redirectQuery = to.query?.redirect
        if (redirectQuery) {
          next({ path: String(redirectQuery) })
        }
        // Redirect to home
        else {
          next({ name: 'home' })
        }
      } else {
        next() // Make sure to always call next()!
      }
    },
  )

  router.beforeEach(
    async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ) => {
      updateMetaTags(to, true)
      next()
    },
  )

  /*
   * This functionality is disabled because it adds a possibility
   * of user to stuck inside questionnaire without possibility to
   * to to the application itself
  router.beforeEach(async (to, from, next) => {
    // Redirect to questionnaire if it wasn't finished
    const step = storage.getItem('sf_oq_step') as string
    // If the step is not 'finish' and we are not at questionnaire - redirect
    if (
      step !== 'questionnaire_finish' &&
      !(to.name as string).includes('questionnaire')
    ) {
      next({ name: step })
    } else {
      next()
    }
  })
  */

  // Check and, if needed, sync the analytics IDs to the backend. We do this
  // here, not in App.vue, so that it will run even if the user does not start
  // a new browser tab/session. In particular, after signup, this will sync the
  // analytics ID on the next route accessed.
  router.beforeEach(
    async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ) => {
      // We don't sync the analytic IDs in the external billing page
      // because we wait to track user data when they come back to the
      // native app.
      if (to.name !== 'billing_apple_external') {
        await maybeSyncAnalyticsIds()
      }
      next()
    },
  )

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  router.afterEach(async (to: RouteLocationNormalized) => {
    if ((window as any)['__PRERENDER_INJECTED']) {
      // Skip Adjust and Mixpanel page tracking during prerendering.
      return
    }

    if (to.name === 'search' && to.query.term && to.query.term !== '') {
      // We track search queries from the Search page beforeRouteLeave() guard.
      return
    }

    if (to.name === 'onboarding') {
      await AdjustWrapper.trackPageViewSignup()
    }

    // Delay a page view call a bit to wait for window.locaion.href to change
    // to the new URL
    // See
    // - https://github.com/vuejs/vue-router/issues/1197
    // - https://github.com/vuejs/vue-router/pull/2292
    // - https://github.com/vuejs/vue-router/issues/2079
    setTimeout(() => {
      Mixpanel.pageView()
    }, 100)
  })

  router.beforeEach(async (to, from, next) => {
    const restoredPosition = appPositionTrackingService.nextPositionOnRedirect(
      to,
      from,
    )
    if (restoredPosition) {
      next(restoredPosition)
      return
    }
    next()
  })

  router.beforeEach(async (to, from, next) => {
    backLink.value.updateHistory(to, from)
    next()
  })

  // Track URL changes so we can restore app position when the user switches to
  // a different app and this app reloads from scratch.
  router.afterEach((route: RouteLocationNormalized) =>
    appPositionTrackingService.storePositionOnChange(route),
  )

  // Initialize notifications.
  // TODO The initialization request can be slow (1s) at times.
  router.afterEach(async (route: RouteLocationNormalized) => {
    // Initialize firebase when the user is authenticated and not on a public page.
    // An extra check is done to see if initialization can happen on the current page
    if (auth.loggedIn() && !route.meta.isPublicPage) {
      await notificationService.init(router, route.path)
    }
  })

  return router
}

export const router = createRouter()
